import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import {
	$allDailyNotesDocuments,
	$createDailyNotesDocument,
	$dailyNotesDocument,
	$hasDailyNotesDocument,
	DAILY_NOTES_DOCUMENT_TYPE,
} from "../../internal/daily-notes";
import {
	$ensureDatePage,
	$ensureTodayPage,
	getTodaySlugPath,
	parseDateSlug,
} from "../../internal/daily-notes/today-page.atom";

export const useDailyNotes = () => {
	const dailyNotesDocument = useAtomValue($dailyNotesDocument);
	const allDailyNotesDocuments = useAtomValue($allDailyNotesDocuments);
	const hasDailyNotesDocument = useAtomValue($hasDailyNotesDocument);
	const createDailyNotesDocumentAtom = useSetAtom($createDailyNotesDocument);

	const createDailyNotesDocument = useCallback(async () => {
		return await createDailyNotesDocumentAtom();
	}, [createDailyNotesDocumentAtom]);

	const getTodaySlugPathValue = useCallback(() => getTodaySlugPath(), []);

	return {
		dailyNotesDocument,
		allDailyNotesDocuments,
		hasMultipleDailyNotesDocuments: allDailyNotesDocuments.length > 1,
		hasDailyNotesDocument,
		createDailyNotesDocument,
		getTodaySlugPath: getTodaySlugPathValue,
	};
};

export const useEnsureTodayPage = (documentId: string | undefined) => {
	const ensureTodayPageAtom = useSetAtom($ensureTodayPage(documentId ?? ""));

	const ensureTodayPage = useCallback(async () => {
		if (!documentId) {
			return undefined;
		}
		return await ensureTodayPageAtom();
	}, [documentId, ensureTodayPageAtom]);

	return {
		ensureTodayPage,
	};
};

/**
 * Hook to ensure a date page exists for a Daily Notes document.
 * If the slugPath is a valid date (YYYY-MM-DD) and the page doesn't exist,
 * this will create the date page.
 */
export const useEnsureDatePage = (documentId: string | undefined, slugPath: string | undefined) => {
	const ensureDatePageAtom = useSetAtom(
		$ensureDatePage({ documentId: documentId ?? "", slugPath: slugPath ?? "" })
	);

	const ensureDatePage = useCallback(async () => {
		if (!documentId || !slugPath) {
			return undefined;
		}
		return await ensureDatePageAtom();
	}, [documentId, slugPath, ensureDatePageAtom]);

	return {
		ensureDatePage,
	};
};

/**
 * Check if a document is a Daily Notes document.
 */
export const isDailyNotesDocument = (type: string | undefined): boolean => {
	return type === DAILY_NOTES_DOCUMENT_TYPE;
};

/**
 * Check if a slug is a valid date slug (YYYY-MM-DD).
 */
export const isValidDateSlug = (slugPath: string | undefined): boolean => {
	if (!slugPath) {
		return false;
	}
	return parseDateSlug(slugPath) !== undefined;
};
